import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface FabricSettings {
  id: string;
  fabricType: string;
  fabricCategory: string | null;
  weldTempHot: number | null;  // Hot Time in seconds
  weldTempCold: number | null; // Cold Time in seconds
  weldPressure: number | null;
  weldNotes: string | null;
  cutBladeType: string | null;
  cutSpeed: number | null;
  cutPressure: number | null;
  cutNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CatalogColor {
  id: string;
  code: string;
  name: string;
  partNumber: string | null;
  rgbaValue: string | null;
  sortOrder: number;
}

interface CatalogType {
  id: string;
  code: string;
  name: string;
  pricingCategory: string;
  group: string;
  sortOrder: number;
  colors: CatalogColor[];
}

const FABRIC_CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'solar', label: 'Solar' },
  { value: 'premium_solar', label: 'Premium Solar' },
  { value: 'cvw', label: 'CVW (Vinyl)' },
  { value: 'insect', label: 'Insect' },
];

const PRICING_CATEGORIES = [
  { value: 'solar94', label: 'Solar' },
  { value: 'solar99', label: 'Premium Solar' },
  { value: 'cvw', label: 'Vinyl' },
  { value: 'insect', label: 'Insect' },
];

const GROUP_LABELS = ['Solar', 'Solar Premium', 'Vinyl', 'Insect'];


export default function FabricSettingsPage() {
  const [activeTab, setActiveTab] = useState<'catalog' | 'weld'>('catalog');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<FabricSettings[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingFabric, setEditingFabric] = useState<FabricSettings | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Catalog state
  const [catalogTypes, setCatalogTypes] = useState<CatalogType[]>([]);
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [showAddColorModal, setShowAddColorModal] = useState<string | null>(null);
  const [editingColor, setEditingColor] = useState<{ typeId: string; color: CatalogColor } | null>(null);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', pricingCategory: 'solar94', groupLabel: 'Solar' });
  const [colorForm, setColorForm] = useState({ code: '', name: '', partNumber: '', rgbaValue: '' });

  // Form state for editing
  const [formData, setFormData] = useState({
    fabricType: '',
    fabricCategory: '',
    weldTempHot: '',   // Hot Time (seconds)
    weldTempCold: '',  // Cold Time (seconds)
    weldPressure: '',
    weldNotes: '',
    cutBladeType: '',
    cutSpeed: '',
    cutPressure: '',
    cutNotes: '',
  });

  useEffect(() => {
    if (activeTab === 'weld') loadSettings();
    else loadCatalog();
  }, [selectedCategory, activeTab]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.fabricSettings.getAll(selectedCategory || undefined);
      setSettings(data);
    } catch (err) {
      setError('Failed to load fabric settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.fabricSettings.getCatalog();
      setCatalogTypes(data.fabricTypes || []);
    } catch (err) {
      setError('Failed to load fabric catalog');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.fabricSettings.createType(typeForm);
      setShowAddTypeModal(false);
      setTypeForm({ code: '', name: '', pricingCategory: 'solar94', groupLabel: 'Solar' });
      setSuccess('Fabric type created');
      loadCatalog();
    } catch (err) {
      setError('Failed to create fabric type');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleType = async (type: CatalogType) => {
    try {
      await api.fabricSettings.updateType(type.id, { active: false });
      setSuccess(`${type.name} deactivated — dealers will no longer see it`);
      loadCatalog();
    } catch (err) {
      setError('Failed to update fabric type');
    }
  };

  const handleCreateColor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showAddColorModal) return;
    setSaving(true);
    try {
      await api.fabricSettings.createColor(showAddColorModal, colorForm);
      setShowAddColorModal(null);
      setColorForm({ code: '', name: '', partNumber: '', rgbaValue: '' });
      setSuccess('Color added');
      loadCatalog();
    } catch (err) {
      setError('Failed to create color');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateColor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingColor) return;
    setSaving(true);
    try {
      await api.fabricSettings.updateColor(editingColor.color.id, colorForm);
      setEditingColor(null);
      setColorForm({ code: '', name: '', partNumber: '', rgbaValue: '' });
      setSuccess('Color updated');
      loadCatalog();
    } catch (err) {
      setError('Failed to update color');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteColor = async (colorId: string) => {
    if (!confirm('Remove this color? This won\'t affect existing orders.')) return;
    try {
      await api.fabricSettings.deleteColor(colorId);
      setSuccess('Color removed');
      loadCatalog();
    } catch (err) {
      setError('Failed to remove color');
    }
  };

  const openEditModal = (fabric: FabricSettings) => {
    setEditingFabric(fabric);
    setFormData({
      fabricType: fabric.fabricType,
      fabricCategory: fabric.fabricCategory || '',
      weldTempHot: fabric.weldTempHot?.toString() || '',
      weldTempCold: fabric.weldTempCold?.toString() || '',
      weldPressure: fabric.weldPressure?.toString() || '',
      weldNotes: fabric.weldNotes || '',
      cutBladeType: fabric.cutBladeType || '',
      cutSpeed: fabric.cutSpeed?.toString() || '',
      cutPressure: fabric.cutPressure?.toString() || '',
      cutNotes: fabric.cutNotes || '',
    });
  };

  const openAddModal = () => {
    setShowAddModal(true);
    setFormData({
      fabricType: '',
      fabricCategory: '',
      weldTempHot: '',
      weldTempCold: '',
      weldPressure: '',
      weldNotes: '',
      cutBladeType: '',
      cutSpeed: '',
      cutPressure: '',
      cutNotes: '',
    });
  };

  const closeModal = () => {
    setEditingFabric(null);
    setShowAddModal(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const data = {
        fabricCategory: formData.fabricCategory || undefined,
        weldTempHot: formData.weldTempHot ? parseFloat(formData.weldTempHot) : undefined,
        weldTempCold: formData.weldTempCold ? parseFloat(formData.weldTempCold) : undefined,
        weldPressure: formData.weldPressure ? parseFloat(formData.weldPressure) : undefined,
        weldNotes: formData.weldNotes || undefined,
        cutBladeType: formData.cutBladeType || undefined,
        cutSpeed: formData.cutSpeed ? parseFloat(formData.cutSpeed) : undefined,
        cutPressure: formData.cutPressure ? parseFloat(formData.cutPressure) : undefined,
        cutNotes: formData.cutNotes || undefined,
      };

      if (showAddModal) {
        await api.fabricSettings.create({ ...data, fabricType: formData.fabricType });
        setSuccess('Fabric settings created successfully');
      } else if (editingFabric) {
        await api.fabricSettings.update(editingFabric.fabricType, data);
        setSuccess('Fabric settings updated successfully');
      }

      closeModal();
      loadSettings();
    } catch (err) {
      setError('Failed to save fabric settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getCategoryBadge = (category: string | null) => {
    const colors: Record<string, string> = {
      solar: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      premium_solar: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      cvw: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      insect: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return colors[category || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const renderCatalogTab = () => (
    <>
      <Card>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Manage fabric types and colors available to dealers. Changes take effect immediately.
          </p>
          <button
            onClick={() => { setShowAddTypeModal(true); setTypeForm({ code: '', name: '', pricingCategory: 'solar94', groupLabel: 'Solar' }); }}
            className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 flex items-center gap-2 whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Fabric Type
          </button>
        </div>
      </Card>

      {catalogTypes.map(type => (
        <Card key={type.id}>
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}
          >
            <div className="flex items-center gap-3">
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedType === type.id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-lg">{type.name}</h3>
                <div className="flex gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getCategoryBadge(type.pricingCategory === 'solar94' ? 'solar' : type.pricingCategory === 'solar99' ? 'premium_solar' : type.pricingCategory)}`}>
                    {type.group}
                  </span>
                  <span className="text-xs text-gray-500">{type.colors.length} color{type.colors.length !== 1 ? 's' : ''}</span>
                  <span className="text-xs text-gray-400 font-mono">{type.code}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleToggleType(type); }}
                className="px-3 py-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded hover:bg-red-200"
                title="Deactivate — hides from dealer portal"
              >
                Deactivate
              </button>
            </div>
          </div>

          {expandedType === type.id && (
            <div className="mt-4 border-t dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-500">Colors</h4>
                <button
                  onClick={() => { setShowAddColorModal(type.id); setColorForm({ code: '', name: '', partNumber: '', rgbaValue: '' }); }}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200"
                >
                  + Add Color
                </button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-700 text-gray-500">
                    <th className="text-left py-2 px-3">Color</th>
                    <th className="text-left py-2 px-3">Code</th>
                    <th className="text-left py-2 px-3">Part #</th>
                    <th className="text-center py-2 px-3">Preview</th>
                    <th className="text-center py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {type.colors.map(color => (
                    <tr key={color.id} className="border-b dark:border-gray-700/50">
                      <td className="py-2 px-3 font-medium">{color.name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-500">{color.code}</td>
                      <td className="py-2 px-3 font-mono">{color.partNumber || '-'}</td>
                      <td className="py-2 px-3 text-center">
                        {color.rgbaValue && (
                          <div className="w-8 h-8 rounded border dark:border-gray-600 mx-auto" style={{ backgroundColor: color.rgbaValue }} />
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => {
                              setEditingColor({ typeId: type.id, color });
                              setColorForm({ code: color.code, name: color.name, partNumber: color.partNumber || '', rgbaValue: color.rgbaValue || '' });
                            }}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded hover:bg-blue-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteColor(color.id)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {type.colors.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-center text-gray-400">No colors configured</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}

      {/* Add Type Modal */}
      {showAddTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Add Fabric Type</h2>
            <form onSubmit={handleCreateType} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input type="text" required value={typeForm.name}
                  onChange={e => setTypeForm({ ...typeForm, name: e.target.value, code: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                  placeholder="e.g., Twitchell Ultra 99" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input type="text" value={typeForm.code}
                  onChange={e => setTypeForm({ ...typeForm, code: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 font-mono text-sm"
                  placeholder="auto-generated from name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pricing Category *</label>
                <select value={typeForm.pricingCategory}
                  onChange={e => {
                    const pc = e.target.value;
                    const gl = pc === 'solar94' ? 'Solar' : pc === 'solar99' ? 'Solar Premium' : pc === 'cvw' ? 'Vinyl' : 'Insect';
                    setTypeForm({ ...typeForm, pricingCategory: pc, groupLabel: gl });
                  }}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700">
                  {PRICING_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display Group</label>
                <select value={typeForm.groupLabel}
                  onChange={e => setTypeForm({ ...typeForm, groupLabel: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700">
                  {GROUP_LABELS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                <button type="button" onClick={() => setShowAddTypeModal(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 disabled:opacity-50">
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Color Modal */}
      {(showAddColorModal || editingColor) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editingColor ? 'Edit Color' : 'Add Color'}</h2>
            <form onSubmit={editingColor ? handleUpdateColor : handleCreateColor} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Name *</label>
                <input type="text" required value={colorForm.name}
                  onChange={e => setColorForm({ ...colorForm, name: e.target.value, code: editingColor ? colorForm.code : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                  placeholder="e.g., Black/Bronze" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input type="text" value={colorForm.code}
                  onChange={e => setColorForm({ ...colorForm, code: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 font-mono text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Part Number</label>
                <input type="text" value={colorForm.partNumber}
                  onChange={e => setColorForm({ ...colorForm, partNumber: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 font-mono"
                  placeholder="e.g., 21108" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Preview Color (RGBA)</label>
                <div className="flex gap-2 items-center">
                  <input type="text" value={colorForm.rgbaValue}
                    onChange={e => setColorForm({ ...colorForm, rgbaValue: e.target.value })}
                    className="flex-1 px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 font-mono text-xs"
                    placeholder="rgba(20,20,20,0.65)" />
                  {colorForm.rgbaValue && <div className="w-10 h-10 rounded border dark:border-gray-600" style={{ backgroundColor: colorForm.rgbaValue }} />}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                <button type="button" onClick={() => { setShowAddColorModal(null); setEditingColor(null); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 disabled:opacity-50">
                  {saving ? 'Saving...' : editingColor ? 'Update' : 'Add Color'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Fabric Settings"
        description="Manage fabric types, colors, and production settings"
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b dark:border-gray-700">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'catalog' ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Fabric Catalog
        </button>
        <button
          onClick={() => setActiveTab('weld')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'weld' ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Weld / Cut Settings
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <p className="text-green-600 dark:text-green-400">{success}</p>
        </Card>
      )}

      {error && !editingFabric && !showAddModal && (
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : activeTab === 'catalog' ? renderCatalogTab() : (
      <>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category Filter
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {FABRIC_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Fabric Settings
          </button>
        </div>
      </Card>

      {/* Settings Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th className="text-left py-3 px-4">Fabric Type</th>
                <th className="text-center py-3 px-4">Category</th>
                <th className="text-center py-3 px-4">Hot Time (s)</th>
                <th className="text-center py-3 px-4">Cold Time (s)</th>
                <th className="text-center py-3 px-4">Blade</th>
                <th className="text-center py-3 px-4">Cut Speed</th>
                <th className="text-center py-3 px-4">Cut PSI</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {settings.map(fabric => (
                <tr key={fabric.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="py-3 px-4 font-medium">{fabric.fabricType}</td>
                  <td className="py-3 px-4 text-center">
                    {fabric.fabricCategory && (
                      <span className={`px-2 py-1 rounded-full text-xs capitalize ${getCategoryBadge(fabric.fabricCategory)}`}>
                        {fabric.fabricCategory.replace(/_/g, ' ')}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center font-mono">
                    {fabric.weldTempHot ? `${fabric.weldTempHot}s` : '-'}
                  </td>
                  <td className="py-3 px-4 text-center font-mono">
                    {fabric.weldTempCold ? `${fabric.weldTempCold}s` : '-'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {fabric.cutBladeType || '-'}
                  </td>
                  <td className="py-3 px-4 text-center font-mono">
                    {fabric.cutSpeed ?? '-'}
                  </td>
                  <td className="py-3 px-4 text-center font-mono">
                    {fabric.cutPressure ? `${fabric.cutPressure}` : '-'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => openEditModal(fabric)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit/Add Modal */}
      {(editingFabric || showAddModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {showAddModal ? 'Add Fabric Settings' : `Edit: ${editingFabric?.fabricType}`}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {showAddModal && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Fabric Type *</label>
                    <input
                      type="text"
                      value={formData.fabricType}
                      onChange={(e) => setFormData({ ...formData, fabricType: e.target.value })}
                      required
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., Twitchell Nano 99"
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={formData.fabricCategory}
                    onChange={(e) => setFormData({ ...formData, fabricCategory: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                  >
                    <option value="">Select category...</option>
                    <option value="solar">Solar</option>
                    <option value="premium_solar">Premium Solar</option>
                    <option value="cvw">CVW (Vinyl)</option>
                    <option value="insect">Insect</option>
                  </select>
                </div>

                <h3 className="text-lg font-semibold mt-6 mb-4 border-b dark:border-gray-700 pb-2">
                  Weld Settings
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hot Time (seconds)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.weldTempHot}
                      onChange={(e) => setFormData({ ...formData, weldTempHot: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., 3.50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Cold Time (seconds)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.weldTempCold}
                      onChange={(e) => setFormData({ ...formData, weldTempCold: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., 2.50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Weld Pressure</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.weldPressure}
                      onChange={(e) => setFormData({ ...formData, weldPressure: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Weld Notes</label>
                  <textarea
                    value={formData.weldNotes}
                    onChange={(e) => setFormData({ ...formData, weldNotes: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                    rows={2}
                    placeholder="Special instructions for welding..."
                  />
                </div>

                <h3 className="text-lg font-semibold mt-6 mb-4 border-b dark:border-gray-700 pb-2">
                  Cut Table Settings
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Blade Type</label>
                    <input
                      type="text"
                      value={formData.cutBladeType}
                      onChange={(e) => setFormData({ ...formData, cutBladeType: e.target.value })}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., Standard, Fine, Heavy Duty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Cut Speed</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.cutSpeed}
                      onChange={(e) => setFormData({ ...formData, cutSpeed: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., 2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Cut Pressure (PSI)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.cutPressure}
                      onChange={(e) => setFormData({ ...formData, cutPressure: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      placeholder="e.g., 45"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Cut Notes</label>
                  <textarea
                    value={formData.cutNotes}
                    onChange={(e) => setFormData({ ...formData, cutNotes: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700"
                    rows={2}
                    placeholder="Special instructions for cutting..."
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
